/**
 * The theme contract: resolution and contrast validation, plus the contract's public surface.
 *
 * theme-customization A1. This is the single source of truth both callers use — the Fastify
 * admin-settings theme service and the React provider/editor. At the head of A2a the file was
 * split by its own section banners, behaviour-preservingly, because A2a and B1 both add to it:
 *
 *   - `theme-catalogue.ts` — core types, stock light/dark maps, token shapes, the 24 roles.
 *   - `theme-schema.ts`    — strict Zod schemas, canonicalization and normalization.
 *   - this file             — `resolveMode` / `resolveTheme` / `validateTheme` / coverage.
 *
 * The two re-exports below keep `packages/shared/src/index.ts` and every existing importer
 * working against `theme.js` exactly as before the split.
 */

import {
  compositeHex,
  contrastRatio,
  gradientMinimumContrast,
  normalizeHexColour,
} from './contrast.js';
import { THEME_PAIRS, pairTokenLeaves, type ColourOperand } from './theme-pairs.js';
import {
  DERIVED_TOKEN_SOURCES,
  STOCK_COLOURS,
  THEME_ROLES,
  TOKEN_OWNER_ROLE,
  type HexColour,
  type ResolvedTheme,
  type ThemeFailure,
  type ThemeMode,
  type ThemeOverrides,
  type ThemePreference,
  type ThemeRoleId,
  type ThemeTokenId,
  type ThemeValidation,
} from './theme-catalogue.js';

export * from './theme-catalogue.js';
export * from './theme-schema.js';

// ─── Resolution ──────────────────────────────────────────────────────────────

export function resolveMode(preference: ThemePreference, systemDark: boolean): ThemeMode {
  if (preference === 'LIGHT') return 'light';
  if (preference === 'DARK') return 'dark';
  return systemDark ? 'dark' : 'light';
}

/**
 * Take the whole two-mode object, select one arm, and return the COMPLETE token -> hex map for
 * that mode — code-owned partners included, because the browser serializer writes the whole map
 * and a consumer of a partner token must see the override that moved it.
 *
 * theme-customization A2a adds a third step: the two DERIVED switch tokens are recomputed from
 * the already-resolved `--primary` / `--input` AFTER the role writes land, never read from a
 * cached hex. That ordering is the whole contract — overriding `interactive` or
 * `control-boundary` has to move the switch with it, in a scoped preview as well as on the root.
 * `DERIVED_TOKEN_SOURCES` records the same dependency as data so `rolesFor` can attribute a
 * failure on one of those pairs back to the role that caused it.
 */
export function resolveTheme(mode: ThemeMode, overrides: ThemeOverrides): ResolvedTheme {
  const resolved: Record<ThemeTokenId, HexColour> = { ...STOCK_COLOURS[mode] };
  const arm = overrides[mode] ?? {};
  for (const role of THEME_ROLES) {
    const value = arm[role.id];
    if (value === undefined) continue;
    const hex = normalizeHexColour(value);
    if (hex === null) throw new Error(`${role.id} is not a six-digit hex colour`);
    for (const write of role.writes) resolved[write.token] = hex;
  }

  // The checked switch track follows the interactive colour in LIGHT only: in dark it is pinned
  // to the stock black from `STOCK_DARK_DIFFERENCES`, because the dark `--primary` is #FFFFFF
  // and a white track under the lime thumb measures 1.16. Its boundary follows the interactive
  // colour in light (same colour as the track, i.e. no visible edge, which is what preserves
  // the appearance of the transparent border it replaced) and the control boundary in dark,
  // where it is what separates a black track from a near-black card.
  if (mode === 'light') resolved['--switch-checked'] = resolved['--primary']!;
  resolved['--switch-checked-boundary'] =
    mode === 'dark' ? resolved['--input']! : resolved['--primary']!;

  return Object.freeze(resolved);
}

// ─── Validation ──────────────────────────────────────────────────────────────

function resolveOperand(operand: ColourOperand, resolved: ResolvedTheme): HexColour {
  if ('token' in operand) return resolved[operand.token];
  if ('hex' in operand) {
    const hex = normalizeHexColour(operand.hex);
    if (hex === null) throw new Error(`pair operand is not a six-digit hex: ${operand.hex}`);
    return hex;
  }
  return compositeHex(
    resolveOperand(operand.composite.foreground, resolved),
    operand.composite.alpha,
    resolveOperand(operand.composite.background, resolved),
  );
}

/**
 * The editable roles a pair's token leaves belong to, used to attribute a contrast failure to
 * the input that caused it.
 *
 * A DERIVED leaf (A2a's two switch tokens) is expanded to the tokens it is computed from, so a
 * failure on the switch track is still reported against `interactive` / `control-boundary`
 * rather than against nothing. One level of expansion is enough and is all that is implemented:
 * no derived token is itself derived from another, which `theme.test.ts` asserts.
 */
function rolesFor(tokens: readonly ThemeTokenId[]): ThemeRoleId[] {
  const out: ThemeRoleId[] = [];
  const add = (token: ThemeTokenId): void => {
    const role = TOKEN_OWNER_ROLE[token];
    if (role && !out.includes(role)) out.push(role);
  };
  for (const token of tokens) {
    add(token);
    for (const source of DERIVED_TOKEN_SOURCES[token] ?? []) add(source);
  }
  return out;
}

/**
 * Evaluate the whole finite pair inventory for each named concrete mode and return EVERY
 * failure, so the editor can link each one to its input rather than surfacing the first.
 *
 * `overrides` is already schema-parsed: an invalid colour is a 400 VALIDATION_ERROR before this
 * call, never a contrast failure. `SYSTEM` is a preference and is never a validation mode.
 *
 * Report-scope records resolve from PINNED LIGHT STOCK in every candidate mode: reports use the
 * same fixed light palette in admin preview, public view and PDF, so a portal override — or a
 * dark candidate — must not move them.
 */
export function validateTheme(
  overrides: ThemeOverrides,
  modes: readonly ThemeMode[],
): ThemeValidation {
  const failures: ThemeFailure[] = [];
  for (const mode of modes) {
    const portal = resolveTheme(mode, overrides);
    for (const record of THEME_PAIRS) {
      const resolved = record.scope === 'report' ? STOCK_COLOURS.light : portal;
      const foreground = resolveOperand(record.foreground, resolved);
      const policy = record.policy;

      if (policy.kind === 'gradient-minimum') {
        const background = record.background;
        if (!('gradient' in background)) {
          throw new Error(`${record.id} has a gradient policy without a gradient background`);
        }
        const worst = gradientMinimumContrast(
          foreground,
          resolveOperand(background.gradient.from, resolved),
          resolveOperand(background.gradient.to, resolved),
        );
        if (worst.ratio < policy.floor) {
          failures.push({
            pairId: record.id,
            mode,
            roleIds: rolesFor(pairTokenLeaves(record)),
            foreground,
            background: worst.background,
            ratio: worst.ratio,
            minimum: policy.floor,
          });
        }
        continue;
      }

      const background = record.background;
      if ('gradient' in background) {
        throw new Error(`${record.id} has a gradient background without a gradient policy`);
      }
      const backgroundHex = resolveOperand(background, resolved);
      const ratio = contrastRatio(foreground, backgroundHex);

      if (policy.kind === 'ratio') {
        if (ratio < policy.floor) {
          failures.push({
            pairId: record.id,
            mode,
            roleIds: rolesFor(pairTokenLeaves(record)),
            foreground,
            background: backgroundHex,
            ratio,
            minimum: policy.floor,
          });
        }
        continue;
      }

      // chart-separation: a direct pass, OR the existing rendered boundary clearing the same
      // floor against BOTH neighbours together with the bound non-colour cue. There is no
      // admin-controlled exception — an editable card surface can still redden this.
      if (ratio >= policy.floor) continue;
      const boundary = resolveOperand(policy.boundary, resolved);
      const boundaryClears =
        policy.cue.length > 0 &&
        contrastRatio(boundary, foreground) >= policy.floor &&
        contrastRatio(boundary, backgroundHex) >= policy.floor;
      if (!boundaryClears) {
        failures.push({
          pairId: record.id,
          mode,
          roleIds: rolesFor(pairTokenLeaves(record)),
          foreground,
          background: backgroundHex,
          ratio,
          minimum: policy.floor,
        });
      }
    }
  }
  return failures.length === 0 ? { valid: true, failures: [] } : { valid: false, failures };
}

// ─── Coverage (consumed by the web parity instrument) ────────────────────────

export type ThemeRoleCoverage =
  | { roleId: ThemeRoleId; covered: true; pairIds: string[] }
  | { roleId: ThemeRoleId; covered: false; exclusion: 'decorative' | 'non-rendered' };

/**
 * For every editable role: the pair IDs that actually depend on one of its tokens, or the
 * classification that excuses it. `apps/web/src/styles/__tests__/theme-pair-coverage.test.ts`
 * asserts that a `rendered` role always has at least one pair and that an excluded role names
 * its `decorative` / `non-rendered` classification — so a role moving from unused to rendered
 * has to arrive with its pair.
 */
export function themeRoleCoverage(): ThemeRoleCoverage[] {
  const byRole = new Map<ThemeRoleId, string[]>();
  for (const record of THEME_PAIRS) {
    for (const roleId of rolesFor(pairTokenLeaves(record))) {
      const list = byRole.get(roleId) ?? [];
      list.push(record.id);
      byRole.set(roleId, list);
    }
  }
  return THEME_ROLES.map((role) => {
    const pairIds = byRole.get(role.id) ?? [];
    if (pairIds.length > 0) return { roleId: role.id, covered: true, pairIds };
    if (role.usage === 'rendered') {
      throw new Error(
        `${role.id} is classified rendered but no pair depends on it — the inventory is incomplete`,
      );
    }
    return { roleId: role.id, covered: false, exclusion: role.usage };
  });
}
